import {
  ElementStatus,
  SelectedElementContainer,
  useLinearTransform,
  ViewElementContainer
} from '@st/document-viewer-kit'

import { ToolMessage } from '@st/document-viewer-kit'

import { MoveEventHandler, PageStructure, useMove, useUpdateBounds } from '@st/document-viewer-kit'
import { toDragPointerEvent } from '@st/document-viewer-kit/src/use-movable'
import { CalculatorTapeAnnotation, CalculatorTapeLineItem } from '@st/sdk'
import { CheckIcon, EditIcon } from '@st/theme'
import { CalculatorExpression } from '@st/util/calculator-expression'
import { cuid } from '@st/util/cuid'
import { clsx } from 'clsx'
import { ReactNode, useRef, useState } from 'react'
import { DeleteIcon } from '~/components/icons'
import { CalculatorTapeToolState } from '~/st-doc-viewer/annotations'
import { useAnnotationsContext } from '~/st-doc-viewer/annotations-context'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '~/st-doc-viewer/floating-toolbar'
import {
  CALCULATOR_TAPE_LAYOUT_CONFIG,
  addLineItemToTape,
  rebuildCalculatorTape
} from '@st/document-annotations'
import { CalculatorTapeView as CalculatorTapeRenderView } from '@st/document-annotations'
import { HTMLSurface, Viewport } from '@st/document-rendering'
import { uuidv7 } from '@st/util/uuidv7'

function CalculatorTapeAnnotationDisplay({ annotation }: { annotation: CalculatorTapeAnnotation }) {
  return (
    <div
      style={{
        position: 'relative',
        width: annotation.bounds.width,
        height: annotation.bounds.height
      }}
    >
      <Viewport
        Surface={HTMLSurface}
        width={annotation.bounds.width}
        height={annotation.bounds.height}
      >
        <CalculatorTapeRenderView annotation={annotation} />
      </Viewport>
    </div>
  )
}

export function CalculatorTapeToolOverlay({
  page,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  activeAnnotation: CalculatorTapeAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  const transform = useLinearTransform()
  const { currentUser } = useAnnotationsContext()

  return (
    <div
      style={{
        position: 'absolute',
        cursor: 'crosshair',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
      }}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

        if (activeAnnotation) {
          if (status == 'editing') {
            // Don't automatically save/discard when clicking outside
            // User must explicitly save or cancel using the toolbar buttons
          } else {
            send({ type: 'deselectAnnotation' })
          }
        } else {
          // Create a new calculator tape draft at the clicked location
          const initialTape: CalculatorTapeAnnotation = rebuildCalculatorTape({
            id: uuidv7(),
            time: new Date().toISOString(),
            type: 'calculator_tape',
            authorId: currentUser?.id,
            pageNumber: page.pageNumber,
            bounds: {
              x: pointerEvent.point.x,
              y: pointerEvent.point.y,
              width: CALCULATOR_TAPE_LAYOUT_CONFIG.container.width,
              height: 0
            },
            title: '',
            lineItems: [],
            total: '0.00'
          })

          send({
            type: 'createAnnotationDraft',
            elementStatus: 'editing',
            annotationDraft: initialTape
          })
        }
      }}
    />
  )
}

export function CalculatorTapeAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: CalculatorTapeAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  switch (status) {
    case 'none':
      return (
        <ViewElementContainer
          key={annotation.id}
          bounds={annotation.bounds}
          onClick={() => {
            send({ type: 'selectAnnotation', annotationId: annotation.id })
          }}
        >
          <CalculatorTapeAnnotationDisplay annotation={annotation} />
        </ViewElementContainer>
      )
    case 'selected':
      return <CalculatorTapeSelected annotation={annotation} send={send} />
    case 'editing':
      return <CalculatorTapeEditor annotation={annotation} send={send} />
  }
}

function CalculatorTapeSelected({
  annotation,
  send
}: {
  annotation: CalculatorTapeAnnotation
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  const { onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onUpdate: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    },
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  return (
    <SelectedElementContainer
      bounds={annotation.bounds}
      onMove={onMove}
      resizable={false}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            icon={<EditIcon className="size-5" />}
            onClick={() => {
              send({ type: 'beginEditingAnnotation', annotationDraft: annotation })
            }}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => {
              send({ type: 'deleteAnnotation', annotationId: annotation.id })
            }}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <CalculatorTapeAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

/**
 * Pure layout component – takes every visual prop as an argument and
 * leaves behaviour to the caller via `renderRow`.
 */
function CalculatorTapeScaffold({
  width,
  height,
  title,
  lineItems,
  total,
  renderRow,
  renderTitle,
  border = true
}: {
  width: number
  height: number
  title: string
  lineItems: CalculatorTapeLineItem[]
  total: string
  renderRow: (item: CalculatorTapeLineItem, index: number) => ReactNode
  renderTitle?: (title: string) => ReactNode
  border?: boolean
}) {
  const layout = CALCULATOR_TAPE_LAYOUT_CONFIG
  const pad = layout.container.padding

  return (
    <div
      style={{
        width,
        height,
        backgroundColor: '#FFFFFF',
        border: border ? '2px solid #000000' : undefined,
        boxSizing: 'border-box',
        padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`
      }}
    >
      {/* Vertical stack */}
      <div className="flex w-full flex-col">
        {/* Title row */}
        <div style={{ height: layout.title.height }} className="flex w-full items-center">
          {renderTitle ? renderTitle(title) : null}
        </div>

        {/* Spacer before first divider */}
        <div
          style={{
            height: layout.dividerHeight
          }}
        />

        {/* Line items */}
        {lineItems.map(renderRow)}

        {/* Divider before total */}
        <HDivider height={CALCULATOR_TAPE_LAYOUT_CONFIG.dividerHeight} />

        {/* Total row */}
        <TotalRow total={total} />
      </div>
    </div>
  )
}

function CalculatorTapeEditor({
  annotation,
  send
}: {
  annotation: CalculatorTapeAnnotation
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const { onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onUpdate: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    },
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  function handleAddLineItem() {
    if (inputValue.trim() !== '') {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: addLineItemToTape(annotation, inputValue)
      })
      setInputValue('')
      inputRef.current!.focus()
    }
  }

  return (
    <SelectedElementContainer
      bounds={annotation.bounds}
      draggable={false}
      resizable={false}
      selectionBorder={false}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            icon={<CheckIcon className="size-5" />}
            onClick={() => {
              send({
                type: 'updateAnnotationDraft',
                annotationDraft: rebuildCalculatorTape(annotation, {
                  removeEmptyLineItems: true
                })
              })
              send({ type: 'deselectAnnotation' })
            }}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => {
              send({ type: 'deleteAnnotation', annotationId: annotation.id })
            }}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <div className="rounded-sm bg-white drop-shadow-lg">
        <CalculatorTapeDragHandle onMove={onMove} />

        {annotation.lineItems.length > 0 && (
          <CalculatorTapeScaffold
            width={annotation.bounds.width}
            height={annotation.bounds.height}
            title={annotation.title || ''}
            lineItems={annotation.lineItems}
            total={annotation.total}
            border={false}
            renderRow={(item, idx) => (
              <EditableLineItemRow
                key={idx}
                value={item}
                onChange={(newItem) => {
                  const newLineItems = [...annotation.lineItems]
                  newLineItems[idx] = newItem

                  send({
                    type: 'updateAnnotationDraft',
                    annotationDraft: rebuildCalculatorTape({
                      ...annotation,
                      lineItems: newLineItems
                    })
                  })
                }}
              />
            )}
            renderTitle={(currentTitle) => (
              <input
                type="text"
                value={currentTitle}
                placeholder="Title"
                className="w-full rounded-sm border-none bg-stone-100 px-0 py-0 placeholder-gray-400 outline-none focus:ring-0"
                onChange={(e) =>
                  send({
                    type: 'updateAnnotationDraft',
                    annotationDraft: rebuildCalculatorTape({ ...annotation, title: e.target.value })
                  })
                }
                style={{
                  fontSize: 10,
                  fontFamily: 'Helvetica',
                  fontKerning: 'none',
                  lineHeight: 1
                }}
              />
            )}
          />
        )}
        <div className="flex flex-col gap-1 px-2 pb-2 pt-1">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus={true}
            ref={inputRef}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleAddLineItem()
                event.preventDefault()
              }
            }}
            className="h-5 w-full rounded-sm border-none px-2 py-0 text-right ring-1 ring-gray-400 focus:ring-gray-600"
            style={{ fontSize: 8, fontFamily: 'Helvetica', lineHeight: 1, fontKerning: 'none' }}
          />

          <CalculatorGrid
            onButtonPress={(value) => {
              if (value == '=') {
                handleAddLineItem()
              } else {
                setInputValue((prevValue) => prevValue + value)
                inputRef.current!.focus()
                requestAnimationFrame(() => {
                  if (inputRef.current) {
                    inputRef.current.selectionStart = inputRef.current.selectionEnd =
                      inputRef.current.value.length
                  }
                })
              }
            }}
          />
        </div>
      </div>
    </SelectedElementContainer>
  )
}

function CalculatorTapeDragHandle({ onMove }: { onMove: MoveEventHandler }) {
  const { ref, onPointerDown, onPointerMove, onPointerUp } = useMove({
    canvasClass: 'drawing-canvas',
    onMove
  })

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="relative -mt-2 h-2 cursor-move rounded-t-sm bg-gray-200"
    />
  )
}

function EditableLineItemRow({
  value,
  onChange
}: {
  value: CalculatorTapeLineItem
  onChange: (newValue: CalculatorTapeLineItem) => void
}) {
  const layout = CALCULATOR_TAPE_LAYOUT_CONFIG
  return (
    <div
      style={{
        height: layout.row.height,
        display: 'flex',
        alignItems: 'center',
        gap: layout.column.gap,
        fontKerning: 'none'
      }}
    >
      <div
        style={{
          flex: layout.column.leftFlex,
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <input
          type="text"
          value={value.description}
          placeholder={value.description == '' ? 'Description' : ''}
          className="my-0 w-full rounded-sm border-none bg-stone-100 px-0 py-0 placeholder-gray-400 outline-none focus:ring-0"
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          style={{
            fontSize: layout.row.fontSize,
            fontFamily: 'Helvetica',
            fontKerning: 'none',
            lineHeight: 1
          }}
        />
      </div>
      <div
        style={{
          flex: layout.column.rightFlex,
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <input
          type="text"
          value={value.amount}
          className="my-0 w-full rounded-sm border-none bg-stone-100 px-0 py-0 text-right placeholder-gray-300 outline-none focus:ring-0"
          placeholder={value.amount == '' ? 'Amount' : ''}
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
          onBlur={() => {
            const evaluated = CalculatorExpression.eval(value.amount)
            if (evaluated) {
              onChange({ ...value, amount: evaluated })
            }
          }}
          style={{
            fontSize: 8,
            fontFamily: 'Helvetica',
            fontKerning: 'none',
            lineHeight: 1,
            color: value.amount.startsWith('-') ? '#CB3A31' : '#000000'
          }}
        />
      </div>
    </div>
  )
}

function TotalRow({ total }: { total: string }) {
  const layout = CALCULATOR_TAPE_LAYOUT_CONFIG
  return (
    <div
      style={{
        height: layout.row.height,
        width: '100%'
      }}
      className="flex items-center"
    >
      <div
        className="font-bold"
        style={{
          fontSize: 8,
          fontFamily: 'Helvetica',
          flex: layout.column.leftFlex,
          fontKerning: 'none'
        }}
      >
        Total
      </div>
      <div
        className="text-right font-bold"
        style={{
          fontSize: 8,
          fontFamily: 'Helvetica',
          flex: layout.column.rightFlex,
          fontKerning: 'none'
        }}
      >
        {total}
      </div>
    </div>
  )
}

function HDivider({ height }: { height: number }) {
  return (
    <div className="flex flex-col" style={{ width: '100%' }}>
      <div style={{ height: height / 2 - 0.5, width: '100%' }} />
      <div style={{ height: 1, width: '100%', backgroundColor: '#000000' }} />
      <div style={{ height: height / 2 - 0.5, width: '100%' }} />
    </div>
  )
}

function CalculatorGrid({ onButtonPress }: { onButtonPress: (character: string) => void }) {
  const BUTTONS_GRID = [
    ['7', '8', '9', 'x'],
    ['4', '5', '6', '÷'],
    ['1', '2', '3', '+'],
    ['0', '.', '=', '-']
  ]

  function normalizeChar(char: string): string {
    if (char === 'x') return '*'
    if (char === '÷') return '/'
    return char
  }

  return (
    <div className="grid grid-cols-4 gap-px bg-white">
      {BUTTONS_GRID.flat().map((char) => {
        const isDigit = /^\d$/.test(char)
        return (
          <button
            key={char}
            onClick={() => onButtonPress(normalizeChar(char))}
            className={clsx(
              'rounded-sm px-2 py-1 text-sm text-black hover:bg-gray-100 focus:outline-none',
              isDigit ? 'bg-gray-200 active:bg-gray-300' : 'bg-gray-300 active:bg-gray-400'
            )}
          >
            {char}
          </button>
        )
      })}
    </div>
  )
}
